import type { IProduct } from '@/types/Product.model';
import i18n from '@/i18n/i18n';
import { extractProductTags } from '@/utils/tags';

/** Product plus optional owner fields used only for compare rows. */
export type CompareProductSource = IProduct & {
  companyName?: string;
  ownerUsername?: string;
};

const LABEL_KEYS: Record<string, string> = {
  articleNumber: 'marketplace.compare.labels.articleNumber',
  availableFormats: 'marketplace.compare.labels.availableFormats',
  productPage: 'marketplace.compare.labels.productPage',
  starCount: 'marketplace.compare.labels.starCount',
  companyName: 'marketplace.compare.labels.companyName',
  ownerUsername: 'marketplace.compare.labels.ownerUsername',
  description: 'marketplace.compare.labels.description',
  views: 'marketplace.compare.labels.views',
  category: 'marketplace.compare.labels.category',
  Tags: 'marketplace.compare.labels.Tags',
  price: 'marketplace.compare.labels.price',
};

/** Humanize metadata / field keys with i18n for known fields. */
export function normalizeAttributeLabel(key: string): string {
  const i18nKey = LABEL_KEYS[key];
  if (i18nKey) return i18n.t(i18nKey);

  const spaced = key
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return key;
  return spaced
    .split(' ')
    .map((word) => {
      if (word.length <= 3 && word === word.toUpperCase()) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/** Strip HTML tags for a plain-text description snippet. */
export function plainTextFromHtml(html: string | undefined | null, maxLen = 160): string {
  if (!html) return '';
  const text = html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 1).trimEnd()}…`;
}

/** Flatten product metadata row into key → display value. */
export function flattenProductMetadata(product: IProduct): Record<string, string> {
  const rows = product.metadata?.rows ?? {};
  const row =
    rows[product._id] ??
    rows['default'] ??
    (Object.keys(rows).length > 0 ? rows[Object.keys(rows)[0]] : undefined);
  const data = row?.data ?? {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value == null) continue;
    const text = String(value).trim();
    if (!text) continue;
    out[key] = text;
  }
  return out;
}

/**
 * Metadata + common product fields for side-by-side compare.
 * Product-level fields override metadata when both exist.
 * Always includes views / stars so the table stays populated.
 */
export function flattenProductCompareAttrs(product: CompareProductSource): Record<string, string> {
  const out = flattenProductMetadata(product);

  const article = product.articleNumber?.trim();
  if (article) out.articleNumber = article;

  const category = product.category?.trim();
  if (category) out.category = category;

  const tags = extractProductTags(product);
  if (tags.length > 0) out.Tags = tags.join(', ');

  const formats = (product.availableFormats ?? []).map((f) => f.trim()).filter(Boolean);
  if (formats.length > 0) out.availableFormats = formats.join(', ');

  const page = product.productPage?.trim();
  if (page) out.productPage = page;

  const company = product.companyName?.trim();
  if (company) out.companyName = company;

  const username = product.ownerUsername?.trim();
  if (username) out.ownerUsername = `@${username.replace(/^@/, '')}`;

  const description = plainTextFromHtml(product.descriptionHtml);
  if (description) out.description = description;

  // Always show engagement metrics (0 is still useful when comparing)
  out.views = String(product.views ?? 0);
  out.starCount = String(product.starCount ?? 0);

  return out;
}

export type MetadataCompareStatus = 'same' | 'different' | 'unique' | 'empty';

export type MetadataCompareRow = {
  key: string;
  label: string;
  values: (string | null)[];
  status: MetadataCompareStatus;
};

function resolveAttributeLabel(key: string, products: CompareProductSource[]): string {
  for (const product of products) {
    const colName = product.metadata?.columns?.[key]?.name?.trim();
    if (colName) {
      // Prefer i18n for known keys; otherwise show the column name as stored
      if (LABEL_KEYS[key]) return normalizeAttributeLabel(key);
      return colName;
    }
  }
  return normalizeAttributeLabel(key);
}

const PREFERRED_KEY_ORDER = [
  'companyName',
  'ownerUsername',
  'articleNumber',
  'category',
  'Tags',
  'price',
  'description',
  'availableFormats',
  'productPage',
  'views',
  'starCount',
];

export function buildMetadataCompareRows(products: CompareProductSource[]): MetadataCompareRow[] {
  if (products.length === 0) return [];

  const flats = products.map(flattenProductCompareAttrs);
  const keySet = new Set<string>();
  for (const flat of flats) {
    for (const key of Object.keys(flat)) keySet.add(key);
  }

  const keys = [...keySet].sort((a, b) => {
    const ai = PREFERRED_KEY_ORDER.indexOf(a);
    const bi = PREFERRED_KEY_ORDER.indexOf(b);
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    }
    return resolveAttributeLabel(a, products).localeCompare(resolveAttributeLabel(b, products));
  });

  return keys.map((key) => {
    const values = flats.map((flat) => flat[key] ?? null);
    const present = values.filter((v): v is string => v != null && v !== '');
    let status: MetadataCompareStatus = 'empty';
    if (present.length === 0) {
      status = 'empty';
    } else if (present.length === 1) {
      status = 'unique';
    } else {
      const first = present[0];
      status = present.every((v) => v === first) ? 'same' : 'different';
    }
    return {
      key,
      label: resolveAttributeLabel(key, products),
      values,
      status,
    };
  });
}
